/**
 * Platform content-type catalogue + spec guidance.
 *
 * For each platform we expose the common content formats (Post, Reel,
 * Carousel, Story, Short, Pin, Article, etc.) and a list of human-readable
 * requirements (max files, dimensions, aspect ratios, caption limits,
 * video duration, file size) that the user should follow.
 *
 * Source: platform publisher docs as of early 2026. Numbers are deliberately
 * conservative — the calendar guidance is meant to keep creatives from
 * shipping assets that will get auto-cropped or rejected, not to be a
 * pixel-perfect spec sheet.
 */

// ── Content-type slugs used across the calendar.  These are *stable* —
// renaming them would orphan saved activities.
export const CONTENT_TYPES = {
  facebook: [
    { key: "post",     label: "Image / Text Post" },
    { key: "carousel", label: "Carousel (multi-image)" },
    { key: "reel",     label: "Reel (short video)" },
    { key: "video",    label: "Native Video" },
    { key: "story",    label: "Story (24h)" },
    { key: "event",    label: "Event" },
    { key: "live",     label: "Live broadcast" },
  ],
  instagram: [
    { key: "post",     label: "Feed Post (single image)" },
    { key: "carousel", label: "Carousel" },
    { key: "reel",     label: "Reel" },
    { key: "story",    label: "Story" },
    { key: "video",    label: "Long-form video" },
  ],
  x: [
    { key: "post",     label: "Post (≤ 280 chars)" },
    { key: "thread",   label: "Thread" },
    { key: "media",    label: "Post with image / video" },
    { key: "poll",     label: "Poll" },
  ],
  linkedin: [
    { key: "post",     label: "Text Post" },
    { key: "image",    label: "Image Post" },
    { key: "carousel", label: "Document / Carousel PDF" },
    { key: "article",  label: "Long-form Article" },
    { key: "video",    label: "Native Video" },
    { key: "poll",     label: "Poll" },
  ],
  tiktok: [
    { key: "video",    label: "Short Video" },
    { key: "carousel", label: "Photo Carousel" },
    { key: "live",     label: "Live" },
  ],
  youtube: [
    { key: "video",    label: "Long-form Video" },
    { key: "short",    label: "YouTube Short" },
    { key: "live",     label: "Live Stream" },
    { key: "premiere", label: "Premiere" },
  ],
  pinterest: [
    { key: "pin",         label: "Standard Pin" },
    { key: "idea_pin",    label: "Idea Pin (multi-page)" },
    { key: "video_pin",   label: "Video Pin" },
    { key: "carousel",    label: "Carousel Pin" },
  ],
  threads: [
    { key: "post",     label: "Post" },
    { key: "media",    label: "Post with media" },
    { key: "thread",   label: "Thread" },
  ],
  google_business: [
    { key: "update",   label: "Update post" },
    { key: "offer",    label: "Offer / Promotion" },
    { key: "event",    label: "Event" },
    { key: "product",  label: "Product post" },
  ],
  wordpress: [
    { key: "post",     label: "Blog Post" },
    { key: "page",     label: "Page" },
    { key: "news",     label: "News article" },
  ],
  blog: [
    { key: "post",     label: "Blog Post" },
    { key: "longform", label: "Long-form / pillar content" },
  ],
  email: [
    { key: "newsletter", label: "Newsletter" },
    { key: "promo",      label: "Promotional broadcast" },
    { key: "welcome",    label: "Welcome / Onboarding" },
    { key: "transactional", label: "Transactional" },
    { key: "drip",       label: "Drip / Sequence step" },
  ],
  sms: [
    { key: "broadcast", label: "Broadcast" },
    { key: "reminder",  label: "Reminder" },
    { key: "promo",     label: "Promo / Offer" },
  ],
  generic: [
    { key: "post",     label: "Post / Update" },
  ],
};


// ── Spec catalogue keyed by `${platform}:${content_type}`.
// `media`: file count + dimensions / aspect ratio + size.
// `copy`:  caption + headline limits.
// `tips`:  free-form best-practice bullets.
//
// All numbers vetted against publicly-documented limits in late 2025 /
// early 2026. Some platforms (TikTok, YouTube Shorts) keep changing —
// figures below stick to widely-cited safe values.
export const CONTENT_SPECS = {
  // ───── Facebook ─────
  "facebook:post":     {
    media: ["1 image or 1 video", "Recommended 1080 × 1080 px (square) or 1200 × 630 px (landscape)", "Max image size: 30 MB", "Max video: 240 min, 10 GB"],
    copy:  ["Caption: up to 63,206 chars (recommended ≤ 80 for engagement)"],
    tips:  ["Square or 4:5 images outperform landscape in mobile feeds", "Lead with a hook in the first line"],
  },
  "facebook:carousel": {
    media: ["2 to 10 cards", "1080 × 1080 px (square) per card", "Each card max 30 MB", "Mix images and videos allowed"],
    copy:  ["Each card: 40 char headline, 20 char description", "Primary text: 125 chars (recommended)"],
    tips:  ["Carousels with 5+ cards perform best — keep the story coherent across cards"],
  },
  "facebook:reel":     {
    media: ["1 vertical video", "9:16 aspect ratio · 1080 × 1920 px", "Length: 15 sec – 90 sec", "Max 4 GB"],
    copy:  ["Caption ≤ 2,200 chars (≤ 100 chars for best reach)"],
    tips:  ["Hook in the first 1.5 seconds", "Add trending audio for the algorithmic boost"],
  },
  "facebook:video":    {
    media: ["1 video", "1080p or higher", "Length up to 240 min, 10 GB", "Vertical 9:16 or square 1:1 for mobile feeds"],
    copy:  ["Caption ≤ 2,200 chars · Title ≤ 65 chars"],
    tips:  ["Add captions/subtitles — 85% of FB videos are watched on mute"],
  },
  "facebook:story":    {
    media: ["1 image or video", "9:16 vertical · 1080 × 1920 px", "Video up to 60 sec", "Max 30 MB image / 4 GB video"],
    copy:  ["Keep text in safe area (avoid top 250px / bottom 250px)"],
    tips:  ["Use stickers and polls to lift engagement"],
  },
  "facebook:event":    {
    media: ["1 cover image", "1920 × 1005 px (16:9)"],
    copy:  ["Event name ≤ 64 chars · Description ≤ 5,000 chars"],
    tips:  ["Set the time zone and add a clear CTA in the description"],
  },
  "facebook:live":     {
    media: ["Live stream", "Up to 1080p · max 8 hours"],
    copy:  ["Stream title ≤ 255 chars"],
    tips:  ["Pre-announce the stream 24 hours ahead · check audio levels"],
  },

  // ───── Instagram ─────
  "instagram:post":    {
    media: ["1 image or video", "1080 × 1350 px (4:5) preferred · or 1:1 square", "Max image 30 MB · video 100 MB"],
    copy:  ["Caption ≤ 2,200 chars · max 30 hashtags"],
    tips:  ["Carousels and Reels outperform single-image posts", "Use 3–5 highly-relevant hashtags rather than the max"],
  },
  "instagram:carousel": {
    media: ["2 to 10 slides", "1080 × 1350 px (4:5) or 1:1 square — same ratio across all slides", "Mix images and videos allowed"],
    copy:  ["Caption ≤ 2,200 chars"],
    tips:  ["First slide is the hook — second slide is the value · last slide should CTA"],
  },
  "instagram:reel":    {
    media: ["1 vertical video", "9:16 · 1080 × 1920 px", "Length: 15 sec – 90 sec (up to 3 min supported)", "Max 4 GB"],
    copy:  ["Caption ≤ 2,200 chars · Cover image strongly recommended"],
    tips:  ["3-second hook is critical · use trending audio · upload a custom cover"],
  },
  "instagram:story":   {
    media: ["1 image or video", "9:16 · 1080 × 1920 px", "Video up to 60 sec per frame", "Image max 30 MB · video 4 GB"],
    copy:  ["Stickers, polls, questions all welcome"],
    tips:  ["Highlight stories that convert by saving them to a permanent Highlight"],
  },
  "instagram:video":   {
    media: ["1 video", "9:16 or 1:1", "Up to 60 min", "Max 4 GB"],
    copy:  ["Caption ≤ 2,200 chars"],
    tips:  ["IGTV-style long-form: tease the topic, then deliver — most viewers drop after 30 sec"],
  },

  // ───── X (Twitter) ─────
  "x:post":           {
    media: ["No media required"],
    copy:  ["Post body ≤ 280 chars (Premium: 25,000)"],
    tips:  ["First line is the headline — most readers don't expand", "Avoid more than 1 link per post"],
  },
  "x:thread":         {
    media: ["Optional media per tweet"],
    copy:  ["Each tweet ≤ 280 chars · plan a clear narrative arc across the thread"],
    tips:  ["Number the tweets (1/, 2/, 3/) so people know how long the thread is", "Pin the first tweet after publishing for max reach"],
  },
  "x:media":          {
    media: ["Up to 4 images OR 1 GIF OR 1 video", "Images: 1200 × 675 px (16:9) recommended · max 5 MB", "Video: max 2 min 20 sec · 1080p · 512 MB"],
    copy:  ["Post body ≤ 280 chars"],
    tips:  ["Alt text on every image — accessibility AND a small reach boost"],
  },
  "x:poll":           {
    media: ["No media"],
    copy:  ["Question ≤ 280 chars · 2–4 options of ≤ 25 chars each"],
    tips:  ["Polls run for 5 min to 7 days · 24 hours is the sweet spot for engagement"],
  },

  // ───── LinkedIn ─────
  "linkedin:post":    {
    media: ["No media (text only)"],
    copy:  ["Body ≤ 3,000 chars · first 200 chars show in feed"],
    tips:  ["Hook in line 1 · then 'see more' — leverage curiosity"],
  },
  "linkedin:image":   {
    media: ["1 image", "1200 × 627 px (1.91:1) for link posts · 1200 × 1200 px for square", "Max 5 MB"],
    copy:  ["Body ≤ 3,000 chars"],
    tips:  ["Native uploads outperform link previews 5–10x on LinkedIn"],
  },
  "linkedin:carousel": {
    media: ["PDF document, 2 to 20 pages", "1080 × 1080 px (1:1) or 1080 × 1350 px (4:5)", "Max 100 MB PDF"],
    copy:  ["Document title ≤ 90 chars · Post body ≤ 3,000 chars"],
    tips:  ["Treat each page like a slide — title + one idea per page"],
  },
  "linkedin:article": {
    media: ["Optional cover image · 1280 × 720 px"],
    copy:  ["Headline ≤ 100 chars · Body up to 110,000 chars (recommended 800–2,000 words)"],
    tips:  ["Add at least 1 image per ~500 words · close with a clear CTA"],
  },
  "linkedin:video":   {
    media: ["1 native video", "16:9 or 1:1 or 9:16 (vertical mobile)", "3 sec – 10 min · max 5 GB"],
    copy:  ["Body ≤ 3,000 chars"],
    tips:  ["Add captions: 80%+ watch with sound off on LinkedIn"],
  },
  "linkedin:poll":    {
    media: ["No media"],
    copy:  ["Question ≤ 140 chars · 2–4 options of ≤ 30 chars each"],
    tips:  ["1-week duration usually performs better than 1-day on LinkedIn"],
  },

  // ───── TikTok ─────
  "tiktok:video":     {
    media: ["1 vertical video", "9:16 · 1080 × 1920 px", "3 sec – 10 min", "Max 287.6 MB (mobile) · 4 GB (web)"],
    copy:  ["Caption ≤ 4,000 chars · 3–5 hashtags work best"],
    tips:  ["Hook in the first second · trending sound is critical for For You page"],
  },
  "tiktok:carousel":  {
    media: ["2 to 35 photos", "1080 × 1920 px (9:16) recommended"],
    copy:  ["Caption ≤ 4,000 chars"],
    tips:  ["Add trending audio — same algo boost as video"],
  },
  "tiktok:live":      {
    media: ["Live stream · vertical 9:16"],
    copy:  ["Title ≤ 32 chars"],
    tips:  ["Need ≥ 1,000 followers to go Live · pre-announce 12–24 hours ahead"],
  },

  // ───── YouTube ─────
  "youtube:video":    {
    media: ["1 video", "16:9 · 1920 × 1080 px (or 4K)", "Up to 12 hours · max 256 GB"],
    copy:  ["Title ≤ 100 chars · Description ≤ 5,000 chars · 15 tags"],
    tips:  ["Custom thumbnail at 1280 × 720 px · first 30 sec drive retention"],
  },
  "youtube:short":    {
    media: ["1 vertical video", "9:16 · 1080 × 1920 px", "Length ≤ 60 sec", "Max 256 GB"],
    copy:  ["Title ≤ 100 chars · use #Shorts in the title or description"],
    tips:  ["Loop the video so viewers rewatch · pin a CTA comment"],
  },
  "youtube:live":     {
    media: ["Live stream · 1080p+"],
    copy:  ["Title ≤ 100 chars · Description ≤ 5,000 chars"],
    tips:  ["Schedule the stream 24 hours ahead so subscribers get notified"],
  },
  "youtube:premiere": {
    media: ["Pre-rendered video that debuts at a scheduled time"],
    copy:  ["Title ≤ 100 chars"],
    tips:  ["Set the premiere 24+ hours in advance · live-chat with the audience as it plays"],
  },

  // ───── Pinterest ─────
  "pinterest:pin":    {
    media: ["1 image", "1000 × 1500 px (2:3 vertical) preferred", "Max 32 MB image · 2 GB video"],
    copy:  ["Title ≤ 100 chars · Description ≤ 500 chars"],
    tips:  ["Vertical 2:3 outperforms square 4:5x · add a clear, branded text overlay"],
  },
  "pinterest:idea_pin": {
    media: ["1 to 20 pages (image or video, 60 sec each)", "9:16 · 1080 × 1920 px"],
    copy:  ["Title ≤ 100 chars · each page can have its own note"],
    tips:  ["Idea Pins are evergreen — they keep surfacing for weeks"],
  },
  "pinterest:video_pin": {
    media: ["1 video", "9:16 (vertical) or 1:1 (square)", "4 sec – 15 min · max 2 GB"],
    copy:  ["Title ≤ 100 chars · Description ≤ 500 chars"],
    tips:  ["Cover frame matters — the first frame is what shows in feed"],
  },
  "pinterest:carousel": {
    media: ["2 to 5 images", "1000 × 1500 px (2:3) per image"],
    copy:  ["Title ≤ 100 chars · Description ≤ 500 chars"],
    tips:  ["Tell a sequential story — first → last pin reveal"],
  },

  // ───── Threads ─────
  "threads:post":     {
    media: ["No media"],
    copy:  ["Body ≤ 500 chars"],
    tips:  ["Threads is conversation-first — ask a question to drive replies"],
  },
  "threads:media":    {
    media: ["Up to 10 images OR 1 video", "Aspect ratio: 1:1 or 4:5", "Image max 5 MB · video max 5 min"],
    copy:  ["Body ≤ 500 chars"],
    tips:  ["Vertical or square works best in the feed"],
  },
  "threads:thread":   {
    media: ["Optional per-post"],
    copy:  ["Each post ≤ 500 chars · chain multiple posts"],
    tips:  ["Quote-reply trick: continue a longer narrative across threads"],
  },

  // ───── Google Business Profile ─────
  "google_business:update": {
    media: ["1 image", "1200 × 900 px (4:3) recommended · min 720 × 540 px"],
    copy:  ["Body ≤ 1,500 chars · headline ≤ 58 chars"],
    tips:  ["Posts auto-expire after 7 days — refresh weekly to stay current"],
  },
  "google_business:offer":  {
    media: ["1 image · 1200 × 900 px"],
    copy:  ["Title ≤ 58 chars · Coupon code, terms, redeem link", "Body ≤ 1,500 chars"],
    tips:  ["Add a clear redemption window and CTA"],
  },
  "google_business:event":  {
    media: ["1 image · 1200 × 900 px"],
    copy:  ["Event title ≤ 58 chars · description ≤ 1,500 chars · start + end date/time"],
    tips:  ["Use 'Event' type for date-stamped happenings — they pin to your profile"],
  },
  "google_business:product": {
    media: ["1 image · 1200 × 900 px"],
    copy:  ["Product name ≤ 58 chars · price · 1,500 char description"],
    tips:  ["Add a 'Buy' or 'Order online' button link for direct conversion"],
  },

  // ───── WordPress / Blog ─────
  "wordpress:post":   {
    media: ["1 featured image", "1200 × 630 px (1.91:1) for OG sharing"],
    copy:  ["Title ≤ 60 chars (SEO) · Meta description ≤ 155 chars · body 800+ words"],
    tips:  ["Internal-link to 2–3 other posts · add alt text to every image"],
  },
  "wordpress:page":   {
    media: ["Hero image · 1920 × 1080 px"],
    copy:  ["Title ≤ 60 chars · Meta description ≤ 155 chars"],
    tips:  ["Above-the-fold value-prop in ≤ 10 words"],
  },
  "wordpress:news":   {
    media: ["Featured image · 1200 × 630 px"],
    copy:  ["Title ≤ 60 chars · publish date prominent"],
    tips:  ["Lead paragraph carries the 5 Ws (who/what/when/where/why)"],
  },
  "blog:post":        {
    media: ["1 cover image · 1200 × 630 px"],
    copy:  ["Title ≤ 60 chars · 800+ word body"],
    tips:  ["Use H2/H3 sub-headings every 200–300 words for scan-ability"],
  },
  "blog:longform":    {
    media: ["Cover image · 1920 × 1080 px · 4–8 inline images"],
    copy:  ["2,000+ words · TOC at top"],
    tips:  ["Pillar content — link from related shorter posts back to this"],
  },

  // ───── Email ─────
  "email:newsletter": {
    media: ["Hero image ≤ 600 px wide · alt text required"],
    copy:  ["Subject ≤ 50 chars · preview text ≤ 90 chars · body ≤ 102 KB"],
    tips:  ["Keep total email under 102 KB — Gmail clips longer messages", "Mobile-first: design for 320–480 px wide"],
  },
  "email:promo":      {
    media: ["1 hero image · 600 px wide"],
    copy:  ["Subject ≤ 50 chars · clear discount in subject"],
    tips:  ["Single CTA · expiry date creates urgency"],
  },
  "email:welcome":    {
    media: ["Logo + optional welcome image"],
    copy:  ["Subject ≤ 50 chars · friendly first-person tone"],
    tips:  ["Triggered immediately after signup · highest open rate of any email"],
  },
  "email:transactional": {
    media: ["Logo only · keep visual chrome light"],
    copy:  ["Subject is descriptive: 'Your receipt #1234'"],
    tips:  ["Plain-text mirror required by some inbox providers"],
  },
  "email:drip":       {
    media: ["Match the visual style of step 1"],
    copy:  ["Each step builds on the previous · ≤ 200 words per email"],
    tips:  ["Space steps 2–4 days apart · always include an unsubscribe link"],
  },

  // ───── SMS ─────
  "sms:broadcast":    {
    media: ["No media (use MMS sparingly)"],
    copy:  ["Body ≤ 160 chars (1 SMS segment) · or ≤ 459 chars (3 segments)"],
    tips:  ["Include opt-out keyword 'STOP' · brand-prefix the message", "Send between 10am–6pm local time"],
  },
  "sms:reminder":     {
    media: ["No media"],
    copy:  ["Body ≤ 160 chars · include date/time + ICS link"],
    tips:  ["24h reminder converts the best · 1h reminder for same-day"],
  },
  "sms:promo":        {
    media: ["No media"],
    copy:  ["Body ≤ 160 chars · short URL with UTM tracking"],
    tips:  ["Promo code in ALL CAPS · clear expiry"],
  },

  // ───── Generic / fallback ─────
  "generic:post":     {
    media: ["No specific requirements"],
    copy:  ["Adjust per the destination platform"],
    tips:  ["When the platform isn't covered, follow the closest analogue above"],
  },
};


export function specFor(platform, contentType) {
  if (!platform || !contentType) return null;
  return CONTENT_SPECS[`${platform}:${contentType}`] || null;
}

export function contentTypesFor(platform) {
  return CONTENT_TYPES[platform] || CONTENT_TYPES.generic;
}
